import React from 'react';
import { useTranslation } from 'react-i18next';
import type { RecentWithdrawalsComponent, WithdrawalItem } from '../../../types/builder';
import { useBuilderStore } from '../../../store/builderStore';
import { ColorPicker } from '../../Common/ColorPicker';
import { ToggleSwitch } from '../../Common/ToggleSwitch';
import { Plus, Trash2 } from 'lucide-react';
import { BANK_OPTIONS } from '../Renderers/BankLogos';

interface RecentWithdrawalsSettingsProps {
  component: RecentWithdrawalsComponent;
}

export const RecentWithdrawalsSettings: React.FC<RecentWithdrawalsSettingsProps> = ({ component }) => {
  const { t } = useTranslation('builder');
  const { updateComponent } = useBuilderStore();

  const handleUpdate = (updates: Partial<RecentWithdrawalsComponent['settings']>) => {
    updateComponent(component.id, {
      settings: { ...component.settings, ...updates },
    });
  };

  const addItem = () => {
    const newItem: WithdrawalItem = {
      id: `item-${Date.now()}`,
      username: '08X-XXX-XXXX',
      amount: '5,000',
      currency: 'บาท',
      time: '',
      bank: 'kbank',
      status: 'success',
    };
    handleUpdate({ items: [...component.settings.items, newItem] });
  };

  const removeItem = (itemId: string) => {
    handleUpdate({
      items: component.settings.items.filter((item) => item.id !== itemId),
    });
  };

  const updateItem = (itemId: string, updates: Partial<WithdrawalItem>) => {
    handleUpdate({
      items: component.settings.items.map((item) =>
        item.id === itemId ? { ...item, ...updates } : item
      ),
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.title')}</label>
        <input
          type="text"
          value={component.settings.title}
          onChange={(e) => handleUpdate({ title: e.target.value })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium mb-1 text-gray-700">{t('settings.recentWithdrawals.pendingLabel')}</label>
          <input
            type="text"
            value={component.settings.pendingLabel || t('settings.recentWithdrawals.defaultPendingLabel')}
            onChange={(e) => handleUpdate({ pendingLabel: e.target.value })}
            className="w-full px-2 py-1.5 bg-white border border-gray-300 rounded-md text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1 text-gray-700">{t('settings.recentWithdrawals.successLabel')}</label>
          <input
            type="text"
            value={component.settings.successLabel || t('settings.recentWithdrawals.defaultSuccessLabel')}
            onChange={(e) => handleUpdate({ successLabel: e.target.value })}
            className="w-full px-2 py-1.5 bg-white border border-gray-300 rounded-md text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1 text-gray-700">{t('settings.recentWithdrawals.amountSuffix')}</label>
          <input
            type="text"
            value={component.settings.amountSuffix || t('settings.recentWithdrawals.defaultAmountSuffix')}
            onChange={(e) => handleUpdate({ amountSuffix: e.target.value })}
            className="w-full px-2 py-1.5 bg-white border border-gray-300 rounded-md text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1 text-gray-700">{t('settings.common.accentColor')}</label>
          <ColorPicker
            color={component.settings.accentColor || '#FFD43B'}
            onChange={(color) => handleUpdate({ accentColor: color })}
          />
        </div>
      </div>

      <div>
        <ToggleSwitch
          label={t('settings.recentWithdrawals.autoGenerateDate')}
          checked={component.settings.autoGenerateDates !== false}
          onChange={(checked) => handleUpdate({ autoGenerateDates: checked })}
        />
        <p className="text-xs text-gray-500 mt-1">
          {t('settings.recentWithdrawals.autoGenerateDateHint')}
        </p>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium">
            {t('settings.recentWithdrawals.withdrawalItems')}
          </label>
          <button
            onClick={addItem}
            className="p-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
          >
            <Plus size={16} />
          </button>
        </div>

        <div className="space-y-3 max-h-[28rem] overflow-y-auto">
          {component.settings.items.map((item, index) => (
            <div key={item.id} className="p-3 bg-gray-800 rounded-lg border border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-400">
                  {t('settings.recentWithdrawals.itemNumber', { n: index + 1 })}
                </span>
                <button
                  onClick={() => removeItem(item.id)}
                  className="p-1 text-red-400 hover:text-red-300"
                >
                  <Trash2 size={14} />
                </button>
              </div>

              <div className="space-y-2">
                <div>
                  <label className="block text-[11px] text-gray-400 mb-1">{t('settings.recentWithdrawals.bank')}</label>
                  <select
                    value={item.bank || 'kbank'}
                    onChange={(e) => updateItem(item.id, { bank: e.target.value })}
                    className="w-full px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    {BANK_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <input
                  type="text"
                  value={item.username}
                  onChange={(e) => updateItem(item.id, { username: e.target.value })}
                  placeholder={t('settings.recentWithdrawals.usernamePlaceholder')}
                  className="w-full px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                />

                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={item.amount}
                    onChange={(e) => updateItem(item.id, { amount: e.target.value })}
                    placeholder={t('settings.recentWithdrawals.amount')}
                    className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <input
                    type="text"
                    value={item.currency}
                    onChange={(e) => updateItem(item.id, { currency: e.target.value })}
                    placeholder={t('settings.recentWithdrawals.currencyPlaceholderBank')}
                    className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <select
                    value={item.status || 'success'}
                    onChange={(e) =>
                      updateItem(item.id, { status: e.target.value as 'pending' | 'success' })
                    }
                    className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="success">{t('settings.recentWithdrawals.statusSuccess')}</option>
                    <option value="pending">{t('settings.recentWithdrawals.statusPending')}</option>
                  </select>
                  <input
                    type="text"
                    value={item.date || ''}
                    onChange={(e) => updateItem(item.id, { date: e.target.value })}
                    placeholder={t('settings.recentWithdrawals.datePlaceholderAuto')}
                    className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.backgroundColor')}</label>
        <ColorPicker
          color={component.settings.backgroundColor}
          onChange={(color) => handleUpdate({ backgroundColor: color })}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.textColor')}</label>
        <ColorPicker
          color={component.settings.textColor}
          onChange={(color) => handleUpdate({ textColor: color })}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">
          {t('settings.recentWithdrawals.itemBackground')}{' '}
          <span className="text-xs text-gray-500">
            {t('settings.recentWithdrawals.itemBackgroundRgbaHint')}
          </span>
        </label>
        <ColorPicker
          color={component.settings.itemBackgroundColor}
          onChange={(color) => handleUpdate({ itemBackgroundColor: color })}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.recentWithdrawals.maxItemsToShow')}</label>
        <input
          type="number"
          min="1"
          max="10"
          value={component.settings.maxItems}
          onChange={(e) => handleUpdate({ maxItems: parseInt(e.target.value) || 1 })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <ToggleSwitch
          label={t('settings.recentWithdrawals.autoScroll')}
          checked={component.settings.autoScroll}
          onChange={(checked) => handleUpdate({ autoScroll: checked })}
        />
        {component.settings.autoScroll && (
          <p className="text-xs text-gray-500 mt-1">
            {t('settings.recentWithdrawals.autoScrollBankHint')}
          </p>
        )}
      </div>

      {component.settings.autoScroll && (
        <div>
          <label className="block text-sm font-medium mb-2">{t('settings.recentWithdrawals.rotationSpeedMs')}</label>
          <input
            type="range"
            min="1000"
            max="10000"
            step="500"
            value={component.settings.scrollSpeed}
            onChange={(e) => handleUpdate({ scrollSpeed: parseInt(e.target.value) })}
            className="w-full"
          />
          <div className="text-xs text-gray-500 mt-1">{component.settings.scrollSpeed}ms</div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.padding')}</label>
        <input
          type="range"
          min="0"
          max="100"
          value={component.settings.padding}
          onChange={(e) => handleUpdate({ padding: parseInt(e.target.value) })}
          className="w-full"
        />
        <div className="text-xs text-gray-500 mt-1">{component.settings.padding}px</div>
      </div>
    </div>
  );
};
